// ============================================================
// Anmeldung und Thema — der kleine Speicher, den jeder Bildschirm liest.
//
// Nach dem Muster von /opt/fragenbuero/frontend/src/store/useStore.ts, aber
// NUR der Auth-Teil (SPEC §5.3): der Plan lebt in usePlanStore, und zwei
// Wahrheiten über denselben Plan sind eine zu viel.
//
// Es gibt hier KEIN "nickname_offen" und keinen 428-Zweig: der Anzeigename
// wird beim ersten Anmelden aus dem Ortsteil der Adresse vorbelegt, ohne
// Eindeutigkeitszwang (T26/A13). Drei Zustände, nicht vier.
// ============================================================
import { create } from "zustand";
import { abmelden as apiAbmelden, holeMich } from "../api/client";
import type { Ich } from "../api/typen";
import { ApiError } from "../lib/fetchJson";
import { readTheme, setTheme, type Theme } from "../lib/theme";

export type AuthStatus = "unbekannt" | "anonym" | "angemeldet";

interface AuthZustand {
  auth: { status: AuthStatus; user: Ich | null };
  thema: Theme;
  /** GET /me — genau EINMAL beim Start (App.tsx, §5.8.1). */
  laden(): Promise<void>;
  /** nach erfolgreichem POST /auth/verify */
  angemeldet(): void;
  abmelden(): Promise<void>;
  themaSetzen(t: Theme): void;
}

/**
 * Der Wegweiser für einen Fehler aus IRGENDEINEM Aufruf.
 *
 * Gibt `true` zurück, wenn er den Fall behandelt hat — dann hört der Aufrufer
 * auf (`if (wegweiser(e)) return;`). Nur der 401-Zweig, denn ein 428 gibt es
 * ohne Spitznamen nicht.
 *
 * **Auch die Autosicherungsschleife in `usePlanStore` ruft ihn** (§5.2.2
 * Regel 6). Ohne das zeichnet der Mensch nach 12 h bzw. 30 Tagen (T26) weiter
 * in einem Editor, der nichts mehr sichert und nicht zur Anmeldung führt
 * (R-20). Der Entwurf im localStorage bleibt dabei liegen — er ist nach der
 * erneuten Anmeldung wieder da.
 */
export function wegweiser(e: unknown): boolean {
  if (!(e instanceof ApiError)) return false;
  if (e.status === 401) {
    useAuthStore.setState({ auth: { status: "anonym", user: null } });
    return true;
  }
  return false;
}

export const useAuthStore = create<AuthZustand>((set) => ({
  // "unbekannt" ist der Startwert und nicht "anonym": der Wächter in App.tsx
  // zeigt darauf ein Wartezeichen, statt einen angemeldeten Menschen für den
  // Bruchteil einer Sekunde auf die Anmeldung zu schicken.
  auth: { status: "unbekannt", user: null },
  thema: readTheme(),

  async laden() {
    try {
      const ich = await holeMich();
      set({ auth: { status: "angemeldet", user: ich } });
    } catch (e) {
      if (wegweiser(e)) return;
      // Jeder andere Fehler (Netz, 503 während eines Deploys) heißt hier
      // ebenfalls "nicht angemeldet": es gibt nichts zu zeigen, und der
      // Wächter führt zur Anmeldung, wo ein erneuter Versuch einen Klick
      // entfernt ist.
      set({ auth: { status: "anonym", user: null } });
    }
  },

  angemeldet() {
    // Der Keks steht; die Person ist angemeldet. `user` holt `laden()`
    // gleich danach — Anmelden.tsx ruft beides in dieser Reihenfolge.
    set({ auth: { status: "angemeldet", user: null } });
  },

  async abmelden() {
    try {
      await apiAbmelden();
    } catch {
      // Auch ein fehlgeschlagener Abmeldeversuch beendet die Sitzung im
      // Browser — alles andere ließe jemanden vor einem Bildschirm sitzen,
      // der so tut, als wäre er noch angemeldet (§5.3).
    }
    set({ auth: { status: "anonym", user: null } });
  },

  themaSetzen(t) {
    setTheme(t);
    set({ thema: t });
  },
}));
